import React from 'react'
import Head from 'next/head'
import MilliplexRoute from '@src/routes/Configurators/products/Milliplex'
import { useIntl } from 'react-intl'
import DefaultLayout, {
  LayoutSpacingOptions,
} from '@src/components/DefaultLayout'
import { NextPage } from 'next'
import ConfiguratorPageError from '@src/routes/Configurators/utils/ConfiguratorPageError'
import getCookiesFromContext from '@src/utils/getCookiesFromContext'
import { PRODUCT_DETAILS } from '@src/queries/ConfiguratorTubesQuery'
import { ProductEnum } from '@src/routes/Configurators/products/Milliplex/enum'
import { getProductStatus } from '@src/routes/Configurators/utils/util'
import { CURRENT_USER } from '@src/queries/CurrentUserQuery'
import { CurrentUserQuery } from '@src/queries/CurrentUserQuery.generated'

interface MilliplexProps {
  countryCode: string
  productCode: number
}

const Milliplex: NextPage<MilliplexProps> = ({ countryCode, productCode }) => {
  const intl = useIntl()
  if (productCode !== 0) {
    return (
      <DefaultLayout spacing={LayoutSpacingOptions.MD}>
        <ConfiguratorPageError
          productCode={productCode}
          country={countryCode}
        />
      </DefaultLayout>
    )
  } else {
    return (
      <React.Fragment>
        <Head>
          <title>
            {intl.formatMessage({
              id: 'CONF_TITLE_MILLIPLEX',
              defaultMessage: 'MILLIPLEX® Analyte Kit Finder',
            })}
          </title>
        </Head>
        <MilliplexRoute />
      </React.Fragment>
    )
  }
}

Milliplex.getInitialProps = async (ctx: ExtendedNextPageContext) => {
  const cookies = getCookiesFromContext(ctx)
  const countryCookie = cookies.get('country') || 'US'
  const languageCookie = cookies.get('language') || 'en'
  let locale: string
  let isCSR: boolean
  let isCSRSupervisor: boolean
  let isGuestShoppingCountry: boolean
  let isOrderableCustomer: boolean
  let isOrderCustomer: boolean
  let isSAPNextEnabled: boolean
  let loggedIn
  let code
  locale = `${languageCookie}_${countryCookie}`
  isCSR = false
  isCSRSupervisor = false
  isSAPNextEnabled = true
  isOrderableCustomer = false
  try {
    let currentUser: CurrentUserQuery | null = null
    const result = await ctx.apolloClient.query({
      query: CURRENT_USER,
    })
    currentUser = result.data
    if (currentUser?.me.__typename === 'LoggedInUser') {
      isCSR = currentUser?.me.roles.isCustomerServiceRep
      isCSRSupervisor = currentUser?.me.roles.isCustomProductCSR
      loggedIn = currentUser?.me.__typename === 'LoggedInUser'
      locale = currentUser?.me.metadata.locale
      isSAPNextEnabled = !!currentUser.me.metadata.isSAPNextEnabled
      isGuestShoppingCountry = !!currentUser.me.metadata.guestShoppingType
      isOrderCustomer =
        currentUser.me.roles.isPublicOrderingCustomer ||
        currentUser.me.roles.isQuoteRequester
      if (isGuestShoppingCountry !== null) {
        isOrderableCustomer = true
      } else if (isOrderCustomer && isGuestShoppingCountry === null) {
        isOrderableCustomer = true
      } else {
        isOrderableCustomer = false
      }
    }
    const inputData = {
      productType: ProductEnum.Milliplex,
      countryCode: countryCookie,
      isCsr: isCSR,
      isCsrSupervisor: isCSRSupervisor,
      isOrderableCustomer: isOrderableCustomer,
      regUser: loggedIn,
      locale: locale,
      sapNextEnabled: isSAPNextEnabled,
    }
    const productDetails = await ctx.apolloClient.query({
      query: PRODUCT_DETAILS,
      variables: { input: inputData },
    })
    if (
      productDetails &&
      productDetails.data.getProductInformation.displayName
    ) {
      code = getProductStatus(
        productDetails.data.getProductInformation.displayName
      )
      return {
        countryCode: countryCookie,
        productCode: code,
      }
    }
  } catch (e) {
    code = 2
    console.error(e)
  }

  return {
    countryCode: countryCookie,
    productCode: code || 0,
  }
}

export default Milliplex
